/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { dashboardAPI } from '../../lib/api';
import { formatDate, formatRelativeTime, TICKET_STATUS, TICKET_TYPES, USER_ROLES } from '../../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import {
  FileText,
  Users,
  Building2,
  Calendar,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Megaphone,
  BarChart3,
  PieChart,
  Loader2,
  Shield,
  Settings,
  Activity,
  AlertTriangle,
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, description, trend, color = 'blue', onClick }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    yellow: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  const content = (
    <Card className={`card-shadow ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
            {description && (
              <p className="text-sm text-slate-500 mt-1">{description}</p>
            )}
            {trend && (
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-emerald-600">{trend}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (onClick) {
    return <div onClick={onClick}>{content}</div>;
  }
  return content;
};

const AdminDashboard = () => {
  const { user } = useAuth();
  usePageTitle('Yonetici Paneli');
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      const [statsRes, ticketsRes, announcementsRes, meetingsRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRecentTickets(5),
        dashboardAPI.getRecentAnnouncements(5),
        dashboardAPI.getUpcomingMeetings(5),
      ]);
      
      setStats(statsRes.data);
      setRecentTickets(ticketsRes.data);
      setRecentAnnouncements(announcementsRes.data);
      setUpcomingMeetings(meetingsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getStatusBadge = (status) => {
    const statusInfo = TICKET_STATUS[status] || { label: status, color: 'default' };
    const colorClasses = {
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      error: 'bg-red-50 text-red-700 border-red-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200',
      default: 'bg-slate-50 text-slate-700 border-slate-200',
    };
    return <Badge className={`${colorClasses[statusInfo.color]} border`}>{statusInfo.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    const typeInfo = TICKET_TYPES[type] || { label: type, color: 'default' };
    const colorClasses = {
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200',
      default: 'bg-slate-50 text-slate-700 border-slate-200',
    };
    return <Badge variant="outline" className={`${colorClasses[typeInfo.color]} border`}>{typeInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header with System Status */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Yönetici Paneli</h1>
          <p className="text-slate-500 mt-1">Kent Konseyi Dijital Katılım Platformu</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg">
            <Activity className="w-4 h-4" />
            <span className="text-sm font-medium">Sistem Aktif</span>
          </div>
          <Link to="/panel/ayarlar">
            <Button variant="outline">
              <Settings className="w-4 h-4 mr-2" />
              Ayarlar
            </Button>
          </Link>
        </div>
      </div>

      {/* Quick Actions - Top */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="quick-actions">
        <Link to="/panel/talepler/yeni">
          <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1.5 hover:border-blue-300 hover:bg-blue-50/50 transition-all">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="text-[11px] font-medium">Yeni Talep</span>
          </Button>
        </Link>
        <Link to="/panel/toplantilar/yeni">
          <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1.5 hover:border-green-300 hover:bg-green-50/50 transition-all">
            <Calendar className="w-5 h-5 text-green-600" />
            <span className="text-[11px] font-medium">Toplanti Olustur</span>
          </Button>
        </Link>
        <Link to="/panel/duyurular/yeni">
          <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1.5 hover:border-amber-300 hover:bg-amber-50/50 transition-all">
            <Megaphone className="w-5 h-5 text-amber-600" />
            <span className="text-[11px] font-medium">Duyuru Yayinla</span>
          </Button>
        </Link>
        <Link to="/panel/anketler/olustur">
          <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1.5 hover:border-sky-300 hover:bg-sky-50/50 transition-all">
            <BarChart3 className="w-5 h-5 text-sky-600" />
            <span className="text-[11px] font-medium">Anket Olustur</span>
          </Button>
        </Link>
        <Link to="/panel/kullanicilar">
          <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1.5 hover:border-purple-300 hover:bg-purple-50/50 transition-all">
            <Users className="w-5 h-5 text-purple-600" />
            <span className="text-[11px] font-medium">Kullanicilar</span>
          </Button>
        </Link>
        <Link to="/panel/stk">
          <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1.5 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all">
            <Building2 className="w-5 h-5 text-indigo-600" />
            <span className="text-[11px] font-medium">STK Basvurulari</span>
          </Button>
        </Link>
        <Link to="/panel/roller">
          <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1.5 hover:border-slate-400 hover:bg-slate-50/50 transition-all">
            <Shield className="w-5 h-5 text-slate-600" />
            <span className="text-[11px] font-medium">Rol Yonetimi</span>
          </Button>
        </Link>
      </div>

      {/* Alert Banner - Pending STK Applications */}
      {stats?.pending_stk_applications > 0 && (
        <Card className="card-shadow border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-900">Bekleyen STK Başvuruları</p>
                  <p className="text-sm text-amber-700">{stats.pending_stk_applications} adet başvuru incelemenizi bekliyor</p>
                </div>
              </div>
              <Link to="/panel/stk">
                <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                  İncele
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/panel/talepler">
          <StatCard
            title="Toplam Talep"
            value={stats?.total_tickets || 0}
            icon={FileText}
            color="blue"
          />
        </Link>
        <Link to="/panel/talepler?status=beklemede">
          <StatCard
            title="Açık Talepler"
            value={stats?.open_tickets || 0}
            icon={AlertCircle}
            color="yellow"
          />
        </Link>
        <Link to="/panel/talepler?status=cozuldu">
          <StatCard
            title="Çözülen Talepler"
            value={stats?.resolved_tickets || 0}
            icon={CheckCircle}
            color="green"
          />
        </Link>
        <StatCard
          title="Ort. Çözüm Süresi"
          value={`${stats?.avg_resolution_days || 0} gün`}
          icon={Clock}
          color="purple"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/panel/kullanicilar">
          <StatCard
            title="Toplam Kullanıcı"
            value={stats?.total_users || 0}
            icon={Users}
            color="blue"
          />
        </Link>
        <Link to="/panel/stk">
          <StatCard
            title="STK Başvuruları"
            value={stats?.total_stk_applications || 0}
            icon={Building2}
            description={`${stats?.pending_stk_applications || 0} beklemede`}
            color="purple"
          />
        </Link>
        <Link to="/panel/toplantilar">
          <StatCard
            title="Toplantılar"
            value={stats?.total_meetings || 0}
            icon={Calendar}
            color="green"
          />
        </Link>
        <Link to="/panel/duyurular">
          <StatCard
            title="Aktif Duyurular"
            value={stats?.total_announcements || 0}
            icon={Megaphone}
            color="yellow"
          />
        </Link>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tickets by Type */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-600" />
              Talep Türü Dağılımı
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.tickets_by_type && Object.keys(stats.tickets_by_type).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.tickets_by_type).map(([type, count]) => {
                  const total = Object.values(stats.tickets_by_type).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  const typeInfo = TICKET_TYPES[type] || { label: type };
                  const colors = { talep: 'bg-blue-600', sikayet: 'bg-amber-600', oneri: 'bg-emerald-600' };
                  
                  return (
                    <div key={type}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">{typeInfo.label}</span>
                        <span className="font-medium">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${colors[type] || 'bg-blue-600'} rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                Henüz talep verisi bulunmuyor
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users by Role */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Kullanıcı Dağılımı
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.users_by_role && Object.keys(stats.users_by_role).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.users_by_role).map(([role, count]) => {
                  const total = Object.values(stats.users_by_role).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  const roleInfo = USER_ROLES[role] || { label: role };
                  
                  return (
                    <div key={role}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">{roleInfo.label}</span>
                        <span className="font-medium">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-600 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                Henüz kullanıcı verisi bulunmuyor
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Items Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Tickets */}
        <Card className="shadow-sm overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between pb-3 border-b bg-slate-50/50">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">Son Talepler</CardTitle>
              </div>
            </div>
            <Link to="/panel/talepler">
              <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 -mr-2">
                Tumu <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {recentTickets.length > 0 ? (
              <div className="divide-y">
                {recentTickets.map((ticket) => {
                  const typeColor = ticket.type === 'sikayet' ? 'bg-red-500' : ticket.type === 'oneri' ? 'bg-emerald-500' : 'bg-blue-500';
                  return (
                    <Link
                      key={ticket.id}
                      to={`/panel/talepler/${ticket.id}`}
                      className="flex items-start gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors group"
                      data-testid={`recent-ticket-${ticket.id}`}
                    >
                      <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${typeColor}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate group-hover:text-blue-600 transition-colors">{ticket.subject}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[11px] text-slate-400 font-mono">{ticket.ticket_no}</span>
                          <span className="text-slate-300">&middot;</span>
                          <span className="text-[11px] text-slate-400">{formatRelativeTime(ticket.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        {getStatusBadge(ticket.status)}
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                  <FileText className="w-5 h-5 text-slate-300" />
                </div>
                <p className="text-sm text-slate-500">Henuz talep bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Announcements */}
        <Card className="shadow-sm overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between pb-3 border-b bg-slate-50/50">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-amber-100">
                <Megaphone className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-base">Son Duyurular</CardTitle>
              </div>
            </div>
            <Link to="/panel/duyurular">
              <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 -mr-2">
                Tumu <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {recentAnnouncements.length > 0 ? (
              <div className="divide-y">
                {recentAnnouncements.map((announcement) => (
                  <Link
                    key={announcement.id}
                    to={`/panel/duyurular/${announcement.id}`}
                    className="flex items-start gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors group"
                    data-testid={`recent-announcement-${announcement.id}`}
                  >
                    <div className="w-1 self-stretch rounded-full flex-shrink-0 bg-amber-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate group-hover:text-amber-600 transition-colors">{announcement.title}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span className="text-[11px] text-slate-400">{formatRelativeTime(announcement.created_at)}</span>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber-500 transition-colors flex-shrink-0 mt-0.5" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                  <Megaphone className="w-5 h-5 text-slate-300" />
                </div>
                <p className="text-sm text-slate-500">Henuz duyuru bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Meetings */}
        <Card className="shadow-sm overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between pb-3 border-b bg-slate-50/50">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-emerald-100">
                <Calendar className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base">Yaklasan Toplantilar</CardTitle>
              </div>
            </div>
            <Link to="/panel/toplantilar">
              <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 -mr-2">
                Tumu <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {upcomingMeetings.length > 0 ? (
              <div className="divide-y">
                {upcomingMeetings.map((meeting) => {
                  const meetingDate = meeting.date ? new Date(meeting.date) : null;
                  const day = meetingDate ? meetingDate.getDate() : '';
                  const month = meetingDate ? meetingDate.toLocaleDateString('tr-TR', { month: 'short' }) : '';
                  return (
                    <Link
                      key={meeting.id}
                      to={`/panel/toplantilar/${meeting.id}`}
                      className="flex items-start gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors group"
                      data-testid={`upcoming-meeting-${meeting.id}`}
                    >
                      <div className="flex flex-col items-center justify-center w-11 h-11 rounded-lg bg-emerald-50 text-emerald-700 flex-shrink-0">
                        <span className="text-sm font-bold leading-none">{day}</span>
                        <span className="text-[10px] uppercase leading-none mt-0.5">{month}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate group-hover:text-emerald-600 transition-colors">{meeting.title}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="text-[11px] text-slate-400">{meeting.time || formatDate(meeting.date)}</span>
                          {meeting.location && (
                            <>
                              <span className="text-slate-300">&middot;</span>
                              <span className="text-[11px] text-slate-400 truncate">{meeting.location}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors flex-shrink-0 mt-0.5" />
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                  <Calendar className="w-5 h-5 text-slate-300" />
                </div>
                <p className="text-sm text-slate-500">Yaklasan toplanti bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
